import { MapperType } from '@/lib/types/utilsT'
import {
  BillingCycleTypes,
  PaymentStatusTypes,
  PlanFeatureCategoryTypes,
  PlanFeaturesTypes,
  PlanStatusTypes,
} from '@/lib/types/PlanT'
import { IconTypes } from '@/components/Icon'
import { gbToBytes } from '@/lib/utils/storageConversion'

export type PlanStatusType = MapperType<PlanStatusTypes>
export const PLAN_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  NEWLY_CREATED: 'newly_created',
} satisfies PlanStatusType

export type BillingCycleType = MapperType<BillingCycleTypes>
export const BILLING_CYCLES = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  BOTH: 'both',
} satisfies BillingCycleType

export const BILLING_CYCLES_CONFIGS: Record<
  BillingCycleType[keyof BillingCycleType],
  {
    label: string
    lowerLabel: string
    order: number
  }
> = {
  [BILLING_CYCLES.MONTHLY]: {
    label: 'Monthly',
    lowerLabel: 'month',
    order: 0,
  },
  [BILLING_CYCLES.YEARLY]: {
    label: 'Yearly',
    lowerLabel: 'year',
    order: 1,
  },
  [BILLING_CYCLES.BOTH]: {
    label: 'Both Options',
    lowerLabel: 'monthly or yearly',
    order: 2,
  },
}

export const PLAN_STATUS_CONFIGS = {
  [PLAN_STATUSES.ACTIVE]: {
    label: 'Active',
    bgClassName: 'bg-primary-2/10 text-primary-2 dark:bg-primary-2/20 dark:text-primary-2',
    pointClassName: 'bg-primary-2',
  },
  [PLAN_STATUSES.INACTIVE]: {
    label: 'Inactive',
    bgClassName: 'bg-accent-1/10 text-accent-1 dark:bg-accent-1/20 dark:text-accent-1',
    pointClassName: 'bg-accent-1',
  },
  [PLAN_STATUSES.NEWLY_CREATED]: {
    label: 'Initialized',
    bgClassName: 'bg-accent-2/10 text-accent-2 dark:bg-accent-2/20 dark:text-accent-2',
    pointClassName: 'bg-accent-2',
  },
}

export type PlanFeatureCategoryType = MapperType<PlanFeatureCategoryTypes>
export const PLAN_FEATURE_CATEGORIES = {
  BASIC: 'basic',
  PREMIUM: 'premium',
  ENTERPRISE: 'enterprise',
} satisfies PlanFeatureCategoryType

export const PLAN_FEATURE_CATEGORY_CONFIGS = {
  [PLAN_FEATURE_CATEGORIES.BASIC]: {
    label: 'Basic',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-200 dark:text-blue-900',
  },
  [PLAN_FEATURE_CATEGORIES.PREMIUM]: {
    label: 'Premium',
    className: 'bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900',
  },
  [PLAN_FEATURE_CATEGORIES.ENTERPRISE]: {
    label: 'Enterprise',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-200 dark:text-orange-900',
  },
}
export type PlanFeaturesType = MapperType<PlanFeaturesTypes>
export const PLAN_FEATURES = {
  // STORAGE_SPACE: 'storage-space',
  // ACTIVE_PROJECTS: 'active-projects',
  // API_INTEGRATIONS: 'api-integrations',
  // ADVANCED_ANALYTICS: 'advanced-analytics',
  // PRIORITY_SUPPORT: 'priority-support',
  // ADVANCED_SECURITY: 'advanced-security',
  // WHITE_LABELING: 'white-labeling',
  COMPANY_MEMBER_AMOUNT: 'company_member_amount',
  VCARDS_AMOUNT: 'vcards_amount',
  CUSTOM_BRANDING: 'custom_branding',
  SMS_AMOUNT: 'sms_amount',
  STORAGE_AMOUNT: 'storage_amount',
} satisfies PlanFeaturesType

export const PLAN_FEATURES_CONFIGS: Record<
  PlanFeaturesType[keyof PlanFeaturesType],
  {
    label: string
    description: string
    icon: IconTypes
    hasLimit: boolean
    category: PlanFeatureCategoryTypes
    defaultLimit?: number
    maxLimit?: number
    unit?: string
    placeholder?: string
    order: number
    amountText?: string
    displayText?: string
  }
> = {
  [PLAN_FEATURES.COMPANY_MEMBER_AMOUNT]: {
    label: 'Team Members',
    description: 'Add team members to collaborate on your workspace',
    icon: 'user-check',
    hasLimit: true,
    category: PLAN_FEATURE_CATEGORIES.BASIC,
    defaultLimit: 5,
    maxLimit: 9999,
    unit: 'members',
    placeholder: 'e.g., 10',
    order: 1,
    amountText: 'Add up to {{amount}} team members',
  },
  [PLAN_FEATURES.VCARDS_AMOUNT]: {
    label: 'Contact Cards',
    description: 'Store and manage your contact information',
    icon: 'credit-card',
    hasLimit: true,
    category: PLAN_FEATURE_CATEGORIES.BASIC,
    defaultLimit: 100,
    maxLimit: 50000,
    unit: 'contacts',
    placeholder: 'e.g., 1000',
    order: 2,
    amountText: 'Save up to {{amount}} contact cards',
  },
  [PLAN_FEATURES.SMS_AMOUNT]: {
    label: 'SMS Messages',
    description: 'Send SMS messages to your contacts',
    icon: 'chat-1',
    hasLimit: true,
    category: PLAN_FEATURE_CATEGORIES.PREMIUM,
    defaultLimit: 1000,
    maxLimit: Infinity,
    unit: 'SMS per month',
    placeholder: 'e.g., 5000',
    order: 3,
    amountText: 'Send up to {{amount}} SMS messages each month',
  },
  [PLAN_FEATURES.STORAGE_AMOUNT]: {
    label: 'Storage Space',
    description: 'Cloud storage for your files and documents',
    icon: 'box',
    hasLimit: true,
    category: PLAN_FEATURE_CATEGORIES.PREMIUM,
    defaultLimit: gbToBytes(10),
    maxLimit: gbToBytes(100),
    unit: 'GB',
    placeholder: 'e.g., 50',
    order: 4,
    amountText: 'Includes {{amount}} GB of cloud storage',
  },
  [PLAN_FEATURES.CUSTOM_BRANDING]: {
    label: 'Custom Branding',
    description: 'Customize the appearance with your own branding and personalize QR codes',
    icon: 'image',
    hasLimit: false,
    category: PLAN_FEATURE_CATEGORIES.PREMIUM,
    unit: 'custom branding',
    order: 5,
    displayText: 'Custom branding & personalized QR codes',
  },
  // [PLAN_FEATURES.ACTIVE_PROJECTS]: {
  //   label: 'Active Projects',
  //   description: 'Number of concurrent projects you can manage',
  //   icon: 'dataflow',
  //   hasLimit: true,
  //   category: PLAN_FEATURE_CATEGORIES.PREMIUM,
  //   defaultLimit: 3,
  //   maxLimit: 100,
  //   unit: 'projects',
  //   placeholder: 'e.g., 3',
  // },
  // [PLAN_FEATURES.API_INTEGRATIONS]: {
  //   label: 'API Integrations',
  //   description: 'Connect with third-party services and tools',
  //   icon: 'link',
  //   hasLimit: true,
  //   category: PLAN_FEATURE_CATEGORIES.ENTERPRISE,
  //   defaultLimit: 5,
  //   maxLimit: 50,
  //   unit: 'integrations',
  //   placeholder: 'e.g., 5',
  // },
  // [PLAN_FEATURES.ADVANCED_ANALYTICS]: {
  //   label: 'Advanced Analytics',
  //   description: 'Detailed insights and reporting on your contacts',
  //   icon: 'search',
  //   hasLimit: false,
  //   category: PLAN_FEATURE_CATEGORIES.PREMIUM,
  // },
  // [PLAN_FEATURES.PRIORITY_SUPPORT]: {
  //   label: 'Priority Support',
  //   description: '24/7 priority customer support',
  //   icon: 'chat',
  //   hasLimit: false,
  //   category: PLAN_FEATURE_CATEGORIES.PREMIUM,
  // },
  // [PLAN_FEATURES.ADVANCED_SECURITY]: {
  //   label: 'Advanced Security',
  //   description: 'Enhanced security features and compliance',
  //   icon: 'lock',
  //   hasLimit: false,
  //   category: PLAN_FEATURE_CATEGORIES.ENTERPRISE,
  // },
  // [PLAN_FEATURES.WHITE_LABELING]: {
  //   label: 'White Labeling',
  //   description: 'Complete white-label solution for your business',
  //   icon: 'star',
  //   hasLimit: false,
  //   category: PLAN_FEATURE_CATEGORIES.ENTERPRISE,
  // },
}

// FORM
export const DROPDOWN_SELECT_BILLING_CYCLES = [
  { value: BILLING_CYCLES.BOTH, title: BILLING_CYCLES_CONFIGS.both.label },
  { value: BILLING_CYCLES.MONTHLY, title: BILLING_CYCLES_CONFIGS.monthly.label },
  { value: BILLING_CYCLES.YEARLY, title: BILLING_CYCLES_CONFIGS.yearly.label },
]

export const DROPDOWN_SELECT_FEATURES = Object.entries(PLAN_FEATURES_CONFIGS).map(
  ([featureKey, feature]) => ({
    ...feature,
    title: feature.label,
    value: featureKey as PlanFeaturesTypes,
  })
)

export const DEFAULT_PLAN_FORM_VALUES = {
  name: '',
  description: '',
  billingCycle: DROPDOWN_SELECT_BILLING_CYCLES[0],
  monthlyPrice: '',
  yearlyPrice: '',
  trial: 0,
  tags: [],
  featuresList: [],
  features: [],
}

export type PaymentStatusType = MapperType<PaymentStatusTypes>
export const PAYMENT_STATUS = {
  DRAFT: 'draft',
  OPEN: 'open',
  PAID: 'paid',
  UNCOLLECTIBLE: 'uncollectible',
  VOID: 'void',
} satisfies PaymentStatusType

export const PAYMENT_STATUS_CONFIGS: Record<
  PaymentStatusType[keyof PaymentStatusType],
  {
    label: string
    className: string
    icon: IconTypes
  }
> = {
  [PAYMENT_STATUS.DRAFT]: {
    label: 'Draft',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300',
    icon: 'edit',
  },
  [PAYMENT_STATUS.OPEN]: {
    label: 'Open',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: 'time',
  },
  [PAYMENT_STATUS.PAID]: {
    label: 'Paid',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: 'check',
  },
  [PAYMENT_STATUS.UNCOLLECTIBLE]: {
    label: 'Uncollectible',
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: 'power',
  },
  [PAYMENT_STATUS.VOID]: {
    label: 'Void',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: 'close-fat',
  },
}
